import { SubmitEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { AlertCircle, KeyRound, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { config } from '@/config';
import { CeremonyDismissed, CeremonyUnsupported, getAssertion } from '@/lib/webauthn';
import { MfaChallenge, MfaMethod } from './mfaChallenge';
import { completeSignIn } from '@/lib/activeAccount';

/**
 * The second half of a gated sign-in (`docs/WebAuthn.md` §4.1).
 *
 * ⚠ **Reached from a 200 that is not a session.** `POST /api/auth/login` answers a gated user
 * with `mfaRequired` and no cookie; a caller that treats any 200 as success signs nobody in and
 * says nothing — silently, and only for the users who opted into protection.
 *
 * Success is deliberately not a callback: redeeming the challenge invalidates `currentUser`,
 * and every caller already reacts to the refreshed user. That keeps one description of "what
 * happens after sign-in" rather than one per caller — the same choice `CredentialsSignInForm`
 * makes.
 */
export function SecondFactorPrompt({
  challenge,
  onCancel,
  onAuthenticated,
}: {
  challenge: MfaChallenge;
  onCancel: () => void;
  /**
   * Overrides the default invalidate. For the session-expiry dialog, which must re-read the
   * identity and replay the parked request rather than just refresh `currentUser` — a session
   * that came back as a *different* user has to tear down, not resume the previous page.
   * Handed the sign-in response, whose `id` is the account that signed in.
   */
  onAuthenticated?: (response: unknown) => void | Promise<void>;
}) {
  const queryClient = useQueryClient();
  // The origin could run no ceremony (the admin console, or a browser without WebAuthn), so a
  // code is the only route. §6: a code substitutes for the *second* factor, never the first.
  // The live challenge: switching method spends the old one and replaces it, so this is state
  // rather than the prop after the first switch.
  const [active, setActive] = useState(challenge);
  const [useCode, setUseCode] = useState(active.mfaRequired === 'recovery_code');
  const [switching, setSwitching] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);
  // ⚠ **Set once the server has *seen* this challenge, which spends it.** Redemption deletes the
  // row before it checks anything — deliberately, so nobody can grind attempts against one id —
  // so after any answered request every remaining control here would 401: "try again", each
  // alternative, and the code form alike. The prompt stops offering routes that cannot work and
  // says what actually has to happen instead.
  const [spent, setSpent] = useState(false);
  // ⚠ A challenge is single-use: redeeming it deletes the row. Firing the ceremony twice — which
  // StrictMode's double-effect does in development — spends it and the second attempt 401s.
  const started = useRef(false);

  const succeed = useCallback(
    async (response: unknown) => {
      if (onAuthenticated) return onAuthenticated(response);
      return completeSignIn(queryClient, response);
    },
    [queryClient, onAuthenticated],
  );

  const runCeremony = useCallback(() => {
    // Bound before the async body: narrowing from a guard does not survive into a closure, so
    // `active.challenge` reads as possibly-undefined inside it however it was checked out here.
    const request = active.challenge;
    if (!request) return;
    setError(undefined);
    setPending(true);
    void (async () => {
      let credential;
      try {
        credential = await getAssertion(request);
      } catch (e) {
        // ⚠ **Nothing was sent, so the challenge is still live.** A prompt the user dismissed
        // never reached the server, which is what makes this the one failure that can honestly
        // offer another go — and why it must not be collapsed with the rejection below.
        if (e instanceof CeremonyUnsupported) console.error('WebAuthn assertion refused', e.cause);
        setError(
          e instanceof CeremonyDismissed
            ? 'That prompt was dismissed. Try again, or use another method.'
            : e instanceof CeremonyUnsupported
              ? `${e.message} Use a recovery code instead.`
              : 'That key could not be used. Try again, or use another method.',
        );
        setPending(false);
        return;
      }

      let session: unknown;
      try {
        ({ data: session } = await axios.post<unknown>(
          `${config.backendApi}/api/auth/mfa/security-key`,
          { challengeId: active.challengeId, credential },
          { withCredentials: true },
        ));
      } catch {
        // The server answered, so the challenge is gone whatever the answer was.
        setSpent(true);
        setError('That could not be verified. Each prompt can only be answered once, so this sign-in has to start again.');
        setPending(false);
        return;
      }
      setPending(false);
      await succeed(session);
    })();
  }, [active, succeed]);

  useEffect(() => {
    if (useCode || started.current) return;
    started.current = true;
    runCeremony();
  }, [useCode, runCeremony]);

  /**
   * Ask the server for a different factor this user holds.
   *
   * ⚠ **Server-side, not a client-side toggle.** Each switch spends the old challenge and mints
   * a fresh one, so exactly one is ever redeemable — flipping a local flag would leave the
   * original live and turn "use something else" into a way to keep retries alive.
   */
  const switchTo = (method: MfaMethod) => {
    setError(undefined);
    setSwitching(true);
    void axios
      .post<MfaChallenge>(
        `${config.backendApi}/api/auth/mfa/method`,
        { challengeId: active.challengeId, method },
        { withCredentials: true },
      )
      .then(
        ({ data }) => {
          setActive(data);
          setUseCode(data.mfaRequired === 'recovery_code');
          // Let the effect re-arm the ceremony for the new challenge.
          started.current = false;
        },
        () => {
          // The switch consumed the outstanding challenge on its way to failing, so there is
          // no longer anything to switch *to*.
          setSpent(true);
          setError('That method could not be offered. This sign-in has to start again.');
        },
      )
      .finally(() => { setSwitching(false); });
  };

  const submitCode = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);
    setPending(true);
    void axios
      .post(
        `${config.backendApi}/api/auth/mfa/recovery-code`,
        { challengeId: active.challengeId, code },
        { withCredentials: true },
      )
      .then(
        ({ data }) => succeed(data),
        () => {
          // ⚠ **The code survives; the prompt does not.** `redeem` marks a code used only on a
          // match, so a mistyped one is still spendable — but the challenge it was presented
          // against is spent either way, so the retry has to happen on a fresh sign-in.
          setSpent(true);
          setError('That code was not accepted. Sign in again to try another — a code is only used up when it works.');
        },
      )
      .finally(() => { setPending(false); });
  };

  return (
    <div className="p-6 space-y-4">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">One more step</h2>
        <p className="text-sm text-muted-foreground">
          {spent
            ? 'This prompt has been used up. Sign in again to get a new one.'
            : useCode
              ? 'Enter one of the recovery codes you saved when you set up your key.'
              : active.mfaRequired === 'passkey'
                ? 'Confirm with your passkey — your device will ask for your PIN, fingerprint, or face.'
                : 'Touch your security key to finish signing in.'}
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {spent ? (
        <Button type="button" className="w-full" data-testid="mfa-start-over" onClick={onCancel}>
          Start over
        </Button>
      ) : useCode ? (
        <form onSubmit={submitCode} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="recovery-code">Recovery code</Label>
            <Input
              id="recovery-code"
              autoComplete="one-time-code"
              autoFocus
              required
              value={code}
              disabled={pending}
              onChange={(e) => { setCode(e.target.value); }}
            />
          </div>
          <Button type="submit" className="w-full" disabled={pending || code.trim() === ''}>
            {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {pending ? 'Checking…' : 'Sign in'}
          </Button>
        </form>
      ) : (
        <Button type="button" className="w-full" disabled={pending} onClick={runCeremony}>
          {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
          {pending ? 'Waiting for your key…' : 'Try again'}
        </Button>
      )}

      {/* Dropped once the challenge is spent: every one of these would 401, and a row of dead
          links reads as "nothing here works" rather than "this attempt is over".
          ⚠ Removed rather than `hidden`, which Tailwind's `flex` utility would override. */}
      {!spent && (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        {/* Only what this user actually holds here — the server decides, so the list never
            offers a method that would fail, and never hints at one they do not have. */}
        {(active.alternatives ?? []).map((alt) => (
          <button
            key={alt}
            type="button"
            className="text-primary hover:underline disabled:opacity-50"
            disabled={switching || pending}
            onClick={() => { switchTo(alt); }}
          >
            {alt === 'passkey' ? 'Use my passkey' : alt === 'security_key' ? 'Use my security key' : 'Use a recovery code'}
          </button>
        ))}
        <button type="button" className="text-muted-foreground hover:underline ml-auto" onClick={onCancel}>
          Start over
        </button>
      </div>
      )}
    </div>
  );
}
